import { GridSortModel } from '@mui/x-data-grid-premium'
import { orderBy } from 'lodash'
import { useCallback, useMemo, useState } from 'react'
import {
  BooleanOperator,
  SortDirection,
  TableFilterInput,
  TableSortInput,
  useGetActiveUsersQuery,
  useGetDetailedTableQuery,
} from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { ActiveUser } from '~/components/settings/types'
import { TableColumnOption } from '~/components/shared/Table/TableColumnAutocomplete'
import {
  DossGridFilterModel,
  buildColumnDataTypeMapForGridFilters,
  dossGridFilterItemsToTableFilterInputs,
  filterOperatorToBooleanOperator,
  swapLoggedInUserFilterArgForUserId,
} from '~/components/tables/filters'
import { getSystemColumnFilterOptions } from '~/components/tables/table-column'
import { DetailedTable, DetailedTableColumnWithTable } from '~/components/tables/types'

interface UseLookupRefinementArgs {
  /** Table being searched (the lookup target). Filters/sorts refine its rows. */
  lookupTableId?: string
  /** Only fetch supporting data and build refinement state while the picker is open. */
  enabled: boolean
}

/**
 * Ad-hoc filter/sort refinement state for a lookup picker, shared by the single-cell popup
 * (LookupRowSearch) and the bulk "Insert Multiple Rows" dialog (BulkSelectLookupModal).
 *
 * Holds the grid filter/sort models the user edits in the Filter & sort panel and derives the
 * query inputs (`lookupFilters` / `lookupSorts` / `lookupFilterBooleanOperator`) that
 * LookupRowOptionsList ANDs with the configured lookup filter expression.
 */
export interface LookupRefinement {
  filterModel: DossGridFilterModel
  setFilterModel: (model: DossGridFilterModel) => void
  sortModel: GridSortModel
  setSortModel: (model: GridSortModel) => void
  /** Clear both models (call when the picker closes). */
  reset: () => void
  filterCount: number
  sortCount: number
  /** True once supporting table data is loaded and there are columns to filter/sort on. */
  isReady: boolean
  detailedTable: DetailedTable | undefined
  activeUsers: ActiveUser[] | undefined
  filterPanelColumns: TableColumnOption[]
  /** Query inputs for LookupRowOptionsList — null when there is no active refinement. */
  lookupFilters: TableFilterInput[] | null
  lookupSorts: TableSortInput[] | null
  lookupFilterBooleanOperator: BooleanOperator | undefined
}

export const useLookupRefinement = ({
  lookupTableId,
  enabled,
}: UseLookupRefinementArgs): LookupRefinement => {
  const { user } = useGlobalContext()
  const [filterModel, setFilterModel] = useState<DossGridFilterModel>({ items: [] })
  const [sortModel, setSortModel] = useState<GridSortModel>([])

  const { data: detailedTableData } = useGetDetailedTableQuery({
    variables: { tableId: lookupTableId ?? '' },
    skip: !enabled || !lookupTableId,
  })
  const detailedTable = detailedTableData?.table
  const parentTableId = detailedTable?.parentTable?.id

  /** Full parent columns are not inlined on getDetailedTable; fetch only when parent filters are needed. */
  const { data: parentTableDetailedData } = useGetDetailedTableQuery({
    variables: { tableId: parentTableId ?? '' },
    skip: !enabled || !parentTableId,
  })
  const parentTableForFilters = parentTableDetailedData?.table

  const { data: activeUsersData } = useGetActiveUsersQuery({ skip: !enabled })

  /** Same ordering as DossTable: this table, parent, then children (by orderOnRecordDetails). */
  const allColumnsForFilterPanel = useMemo((): DetailedTableColumnWithTable[] => {
    if (!detailedTable) {
      return []
    }
    const sortedMain = orderBy(detailedTable.columns, 'order', 'asc').map((col) => ({
      ...col,
      table: { id: detailedTable.id, name: detailedTable.name },
    }))
    const sortedParent =
      parentTableForFilters != null
        ? orderBy(parentTableForFilters.columns, 'order', 'asc').map((col) => ({
            ...col,
            table: { id: parentTableForFilters.id, name: parentTableForFilters.name },
          }))
        : []
    const childTablesOrdered = orderBy(
      detailedTable.childTables,
      (childTable) => childTable.orderOnRecordDetails,
    )
    const sortedChild: DetailedTableColumnWithTable[] = []
    for (const childTable of childTablesOrdered) {
      sortedChild.push(
        ...orderBy(childTable.columns, 'order', 'asc').map((col) => ({
          ...col,
          table: { id: childTable.id, name: childTable.name },
        })),
      )
    }
    return [...sortedMain, ...sortedParent, ...sortedChild]
  }, [detailedTable, parentTableForFilters])

  const filterPanelColumns = useMemo((): TableColumnOption[] => {
    if (!detailedTable || allColumnsForFilterPanel.length === 0) {
      return []
    }
    return [
      ...allColumnsForFilterPanel,
      ...getSystemColumnFilterOptions(detailedTable.id, detailedTable.name),
    ]
  }, [detailedTable, allColumnsForFilterPanel])

  // Build from filterPanelColumns so the system-generated columns are covered explicitly, rather
  // than relying on buildColumnDataTypeMapForGridFilters to inject them internally. A filter item
  // whose field is missing from this map is dropped in conversion, so the system columns must be here.
  const columnDataTypeMap = useMemo(
    () => buildColumnDataTypeMapForGridFilters(filterPanelColumns),
    [filterPanelColumns],
  )

  const cleanedLookupFilters = useMemo(
    (): TableFilterInput[] =>
      dossGridFilterItemsToTableFilterInputs(filterModel.items, columnDataTypeMap),
    [filterModel, columnDataTypeMap],
  )

  /** Same as main table queries: resolve "Logged In User" to the real user id for API filters. */
  const lookupFiltersForQuery = useMemo(
    () =>
      cleanedLookupFilters.length > 0
        ? swapLoggedInUserFilterArgForUserId(cleanedLookupFilters, user?.id)
        : [],
    [cleanedLookupFilters, user?.id],
  )

  const lookupSorts = useMemo(
    (): TableSortInput[] =>
      sortModel.map((item) => ({
        columnId: item.field,
        direction: item.sort === 'desc' ? SortDirection.DESC : SortDirection.ASC,
      })),
    [sortModel],
  )

  const lookupFilterBooleanOperator =
    cleanedLookupFilters.length > 1
      ? filterOperatorToBooleanOperator(filterModel.logicOperator)
      : undefined

  const reset = useCallback(() => {
    setFilterModel({ items: [] })
    setSortModel([])
  }, [])

  return {
    filterModel,
    setFilterModel,
    sortModel,
    setSortModel,
    reset,
    filterCount: filterModel.items.length,
    sortCount: sortModel.length,
    isReady: !!detailedTable && filterPanelColumns.length > 0,
    detailedTable,
    activeUsers: activeUsersData?.activeUsers,
    filterPanelColumns,
    lookupFilters: lookupFiltersForQuery.length > 0 ? lookupFiltersForQuery : null,
    lookupSorts: lookupSorts.length > 0 ? lookupSorts : null,
    lookupFilterBooleanOperator,
  }
}
